import { authService } from '../../../services/auth.ts';
import { SecretService, Secret, Tag } from '../../../services/secret.ts';
import { ToastService } from '../../../services/toast.ts';
import { SubNav } from '../../../components/library/ui/sub-nav.ts';
import '../../../components/library/ui/app-card.ts';
import '../../../components/library/ui/app-button.ts';
import '../../../components/library/ui/app-badge.ts';
import '../../../components/library/ui/app-modal.ts';
import '../../../components/library/ui/app-icon.ts';
import '../../../components/library/ui/app-tag.ts';

export class AppSecrets extends HTMLElement {
  private _secretService = SecretService.getInstance();
  private _toastService = ToastService.getInstance();
  
  private _orgId: string = '';
  private _secrets: Secret[] = [];
  private _orgTags: Tag[] = [];
  private _isLoading: boolean = false;
  private _isFetching: boolean = false;
  private _isCreating: boolean = false;
  private _editingSecret: Secret | null = null;
  private _currentEnv: string = 'dev';
  private _error: string | null = null;
  private _forbidden: boolean = false;
  private _isAdmin: boolean = false;
  private _tagsLoaded: boolean = false;
  private _selectedTags: string[] = [];

  static get observedAttributes() {
    return ['org-id'];
  }

  constructor() {
    super();
    const tab = localStorage.getItem('crate_secret_env');
    if (tab && ['dev', 'staging', 'prod'].includes(tab)) {
      this._currentEnv = tab;
    }
  }

  attributeChangedCallback(name: string, oldVal: string, newVal: string) {
    if (name === 'org-id' && oldVal !== newVal) {
      console.log('AppSecrets: Organization ID changed to:', newVal);
      this._orgId = newVal;
      if (this.isConnected) {
        this.loadSecrets();
      }
    }
  }

  async connectedCallback() {
    this.render();
    
    try {
      // Use attribute if present, otherwise fallback to session/local
      const attrOrgId = this.getAttribute('org-id');
      if (attrOrgId) {
        this._orgId = attrOrgId;
      } else {
        const user = authService.getUser();
        this._orgId = user?.org_id || '';
      }
      
      // If still missing, fetch profile as final fallback
      if (!this._orgId) {
        const profile = await authService.getProfile();
        this._orgId = profile?.org_id || '';
      }
      
      if (!this._orgId) {
        console.warn('AppSecrets: No organization context found after profile fetch');
        this._isLoading = false;
        this.render();
        return;
      }

      await this.loadSecrets();
    } catch (e) {
      console.error('Secrets initialization failed:', e);
      this._isLoading = false;
      this.render();
    }
    
    this.emitPanel();
  }

  private emitPanel() {
    const subNav = document.createElement('app-sub-nav') as SubNav;
    subNav.title = 'Environments';
    subNav.items = [
      { id: 'dev', name: 'Development', icon: 'code' },
      { id: 'staging', name: 'Staging', icon: 'test-tube' },
      { id: 'prod', name: 'Production', icon: 'shield' }
    ];
    subNav.activeId = this._currentEnv;

    subNav.addEventListener('nav-change', ((e: CustomEvent) => {
      this._currentEnv = e.detail.id;
      localStorage.setItem('crate_secret_env', this._currentEnv);
      this.loadSecrets();
      this.emitPanel();
    }) as any);

    this.dispatchEvent(new CustomEvent('shell-panel-changed', {
      detail: { 
        panel: subNav,
        header: {
          title: 'Secrets',
          subtitle: `Encrypted environment variables for ${this._currentEnv.toUpperCase()}`
        }
      },
      bubbles: true,
      composed: true
    }));
  }

  private async loadSecrets() {
    if (!this._orgId) return;
    if (this._isFetching) return;

    this._isFetching = true;
    this._isLoading = true;
    this._error = null;
    this._forbidden = false;
    this.render();
    
    try {
      if (!this._tagsLoaded) {
          const [secrets, tags] = await Promise.all([
              this._secretService.list(this._orgId, this._currentEnv),
              this._secretService.listTags(this._orgId)
          ]);
          this._secrets = secrets;
          this._orgTags = tags || [];
          this._tagsLoaded = true;
      } else {
          this._secrets = await this._secretService.list(this._orgId, this._currentEnv);
      }
      console.log('AppSecrets: Loading sequence complete');
    } catch (e: any) {
      console.error('[AppSecrets] Failed to load secrets:', e);
      if (e.status === 403) {
        this._forbidden = true;
        this._error = "Forbidden: You do not have permission to access secrets for this organization.";
        try {
            const profile = await authService.getProfile();
            const org = profile?.organizations?.find((o: any) => o.id === this._orgId);
            this._isAdmin = org && (org.role === 'admin' || org.role === 'owner');
        } catch (err) {}
      } else {
        this._error = e.message || "An unknown error occurred.";
      }
      // Toast handled by ApiClient
    } finally {
      this._isLoading = false;
      this._isFetching = false;
      this.render();
    }
  }

  private async handleDelete(name: string) {
    if (!confirm(`Are you sure you want to delete secret "${name}"?`)) return;
    
    try {
      await this._secretService.delete(this._orgId, this._currentEnv, name);
      await this.loadSecrets();
    } catch (e: any) {
      // Toast handled by ApiClient
    }
  }

  private openCreateModal(secret: Secret | null = null) {
    this._editingSecret = secret;
    this._selectedTags = secret?.tags ? [...secret.tags] : [];
    this._isCreating = true;
    this.render();
  }

  private closeCreateModal() {
    this._isCreating = false;
    this._editingSecret = null;
    this.render();
  }

  private async handleSave(e: Event) {
    e.preventDefault();
    const form = e.target as HTMLFormElement;
    const formData = new FormData(form);
    const name = formData.get('name') as string;
    const value = formData.get('value') as string;
    const tagsList = form.querySelector('form-dynamic-list[name="tags"]') as any;
    const tags = tagsList?.values || [];

    const submitBtn = form.querySelector('app-button[type="submit"]') as any;
    
    try {
      if (submitBtn) submitBtn.loading = true;
      
      if (this._editingSecret && !value.trim()) {
         // If editing and no value provided, only update metadata
         await this._secretService.updateSecret(this._orgId, this._currentEnv, this._editingSecret.name, name, tags);
      } else {
         if (!value.trim()) throw new Error("Value is required for new secrets");
         const publicKey = await this._secretService.getPublicKey(this._orgId);
         const encryptedValue = await this.encryptLocally(publicKey, value);
         await this._secretService.store(this._orgId, this._currentEnv, name, encryptedValue, true, tags);
      }
      
      this.closeCreateModal();
      
      // Update local tags cache with any new tags created
      for (const t of tags) {
        if (typeof t === 'string' && t.trim() !== '') {
          if (!this._orgTags.some((ot: any) => ot.name === t)) {
             this._orgTags.push({ id: '', organization_id: this._orgId, name: t, metadata: {} });
          }
        }
      }

      await this.loadSecrets();
    } catch (e: any) {
      // Toast handled by ApiClient
    } finally {
      if (submitBtn) submitBtn.loading = false;
    }
  }

  private async encryptLocally(publicKeyPem: string, value: string): Promise<string> {
    const pemContents = publicKeyPem
      .replace("-----BEGIN PUBLIC KEY-----", "")
      .replace("-----END PUBLIC KEY-----", "")
      .replace(/\s/g, "");

    const binaryDerString = window.atob(pemContents);
    const binaryDer = new Uint8Array(binaryDerString.length);
    for (let i = 0; i < binaryDerString.length; i++) {
      binaryDer[i] = binaryDerString.charCodeAt(i);
    }

    const publicKey = await window.crypto.subtle.importKey(
      "spki",
      binaryDer.buffer,
      { name: "RSA-OAEP", hash: "SHA-256" },
      true,
      ["encrypt"]
    );

    const encryptedBuffer = await window.crypto.subtle.encrypt(
      { name: "RSA-OAEP" },
      publicKey,
      new TextEncoder().encode(value)
    );

    const encryptedArray = new Uint8Array(encryptedBuffer);
    let binary = '';
    for (let i = 0; i < encryptedArray.byteLength; i++) {
      binary += String.fromCharCode(encryptedArray[i]);
    }
    return window.btoa(binary);
  }

  private _renderTimeout: any = null;

  render() {
    if (this._renderTimeout) return;
    this._renderTimeout = setTimeout(() => {
        this._renderInternal();
        this._renderTimeout = null;
    }, 0);
  }

  private _renderInternal() {
    if (!this.querySelector('.main-secrets-container')) {
        this.innerHTML = `
            <div class="main-secrets-container"></div>
            <div class="modal-container"></div>
        `;
    }

    const mainContainer = this.querySelector('.main-secrets-container')!;
    const modalContainer = this.querySelector('.modal-container')!;

    if (this._forbidden) {
      mainContainer.innerHTML = this.renderForbidden();
      modalContainer.innerHTML = '';
      return;
    }

    mainContainer.innerHTML = `
      <style>
        .secrets-container {
          padding: 12px 0 0 0;
          position: relative;
          width: 100%;
          margin: 0;
          animation: slideUp 0.4s ease-out;
        }
        
        .secret-list {
          display: grid;
          gap: 16px;
        }

        .secret-item {
          padding: 20px;
          background: rgba(255,255,255,0.02);
          display: flex;
          justify-content: space-between;
          align-items: center;
          position: relative;
          transition: all 0.2s ease;
        }

        .secret-item:hover {
          background: rgba(255,255,255,0.04);
        }

        .secret-info {
          display: flex;
          align-items: center;
          gap: 16px;
        }

        .secret-name {
          font-family: var(--font-sketch);
          font-size: 1.1rem;
          color: var(--text-main);
        }

        .secret-meta {
          font-size: 0.7rem;
          color: var(--text-dim);
          font-family: var(--font-mono);
          margin-top: 4px;
          opacity: 0.8;
        }

        .loader-container {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          padding: 100px 0;
          color: var(--text-dim);
          font-family: var(--font-mono);
        }

        .sketch-border {
          position: relative;
        }

        .sketch-border::before {
          content: "";
          position: absolute;
          top: 0; left: 0; right: 0; bottom: 0;
          border: 1px solid var(--border-sketch);
          filter: url(#sketchy-secrets);
          pointer-events: none;
          z-index: 1;
        }
      </style>

      <svg style="position: absolute; width: 0; height: 0;">
        <defs>
          <filter id="sketchy-secrets" x="-10%" y="-10%" width="120%" height="120%">
            <feTurbulence type="turbulence" baseFrequency="0.02" numOctaves="3" seed="2" result="noise" />
            <feDisplacementMap in="SourceGraphic" in2="noise" scale="3" xChannelSelector="R" yChannelSelector="G" />
          </filter>
        </defs>
      </svg>

      <div class="secrets-container">
        <div style="display: flex; justify-content: flex-end; align-items: center; margin-bottom: 24px;">
          <app-button variant="white" id="btn-new-secret" icon="plus" size="sm" data-tooltip="New Secret"></app-button>
        </div>

        ${this._error ? `
          <div style="padding: 80px 40px; text-align: center;">
            <div style="font-size: 4rem; margin-bottom: 24px;">🔒</div>
            <h1 style="font-family: var(--font-sketch); font-size: 2.5rem; margin-bottom: 16px;">Connection Error</h1>
            <p style="color: var(--text-muted); max-width: 500px; margin: 0 auto 32px auto; font-family: var(--font-mono); font-size: 0.9rem;">
              ${this._error}
            </p>
            <app-button variant="primary" size="sm" onclick="window.location.reload()">Retry Connection</app-button>
          </div>
        ` : !this._orgId ? `
          <div style="padding: 80px 40px; text-align: center;">
            <div style="font-size: 4rem; margin-bottom: 24px;">⛔</div>
            <h1 style="font-family: var(--font-sketch); font-size: 2.5rem; margin-bottom: 16px;">Access Denied</h1>
            <p style="color: var(--text-muted); max-width: 500px; margin: 0 auto 32px auto;">
              We couldn't identify your organization context. Please ensure you have an active organization selected.
            </p>
            <app-button variant="primary" size="sm" onclick="window.location.reload()">Retry Connection</app-button>
          </div>
        ` : this._isLoading ? `
          <div class="loader-container">
            <div style="margin-bottom: 16px;">CONNECTING_TO_VAULT...</div>
            <div style="width: 200px; height: 2px; background: var(--border); overflow: hidden; position: relative;">
              <div style="width: 50%; height: 100%; background: var(--accent); position: absolute; animation: loader-slide 1.5s infinite ease-in-out;"></div>
            </div>
          </div>
          <style>
            @keyframes loader-slide {
              0% { left: -50%; }
              100% { left: 100%; }
            }
          </style>
        ` : this._secrets.length === 0 ? `
          <app-card padding="lg">
            <div style="text-align: center; padding: 40px;">
              <div style="margin-bottom: 24px; display: flex; justify-content: center; opacity: 0.8;">
                <svg viewBox="0 0 24 24" fill="none" stroke="var(--accent)" stroke-width="1.5" style="width: 120px; height: 120px;">
                  <g style="filter: url(#sketchy-secrets);">
                    <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                    <path d="M7 11V7a5 5 0 0 1 10 0v4" />
                  </g>
                </svg>
              </div>
              <p style="margin: 0; color: var(--text-muted); font-family: var(--font-sketch); font-size: 1.2rem;">Vault is empty.</p>
              <p style="font-size: 0.8rem; color: var(--text-dim); margin-top: 12px; max-width: 400px; margin-left: auto; margin-right: auto; font-family: var(--font-mono);">
                Secrets are encrypted environment variables used by your gateway rules. They are never stored in plain text.
              </p>
            </div>
          </app-card>
        ` : `
          <div class="secret-list">
            ${this._secrets.map(s => `
              <div class="secret-item sketch-border">
                <div class="secret-info">
                  <div style="width: 32px; height: 32px; display: grid; place-items: center; background: rgba(255,255,255,0.05); border: 1px dashed var(--border); border-radius: 4px; color: var(--text-dim); font-family: var(--font-mono); font-size: 0.8rem;">
                    *
                  </div>
                  <div>
                    <div class="secret-name" style="display: flex; align-items: center; gap: 8px;">
                      ${s.name}
                      <div class="secret-tags" style="display: flex; gap: 4px; margin-top: 2px;">
                        ${(s.tags || []).map(tName => {
                           const tag = this._orgTags.find(t => t.name === tName) || { name: tName };
                           const safeTagJson = JSON.stringify(tag).replace(/"/g, '&quot;');
                           return `<app-tag tag="${safeTagJson}"></app-tag>`;
                        }).join('')}
                      </div>
                    </div>
                    <div class="secret-meta">V${s.version} • UPDATED ${s.updated_at ? new Date(s.updated_at).toLocaleDateString() : 'N/A'}</div>
                  </div>
                </div>
                <div style="display: flex; gap: 8px;">
                  <app-button variant="ghost" size="sm" class="edit-btn" data-name="${s.name}" data-tooltip="Edit">
                    <app-icon name="edit" size="18"></app-icon>
                  </app-button>
                  <app-button variant="ghost" size="sm" class="delete-btn" data-name="${s.name}" data-tooltip="Delete">
                    <app-icon name="trash" size="18" style="color: #ef4444; opacity: 0.8;"></app-icon>
                  </app-button>
                </div>
              </div>
            `).join('')}
          </div>
        `}
      </div>
    `;

    if (this._isCreating) {
        modalContainer.innerHTML = `
          <app-modal id="secret-modal" open>
            <div slot="header" style="font-family: var(--font-sketch);">${this._editingSecret ? 'Edit Secret' : 'Create New Secret'}</div>
            
            <div style="display: grid; gap: 20px; padding: 10px 0;">
              <p style="color: var(--text-muted); font-size: 0.8rem; font-family: var(--font-mono); margin: 0;">
                Secrets are client-side encrypted before storage.
              </p>
              
              <form id="secret-form">
                <div style="margin-bottom: 20px;">
                  <label style="display: block; margin-bottom: 8px; font-size: 0.7rem; color: var(--text-muted); font-family: var(--font-mono);">NAME</label>
                  <input type="text" name="name" 
                         value="${this._editingSecret?.name || ''}" 
                         placeholder="e.g. DATABASE_URL" required
                         style="width: 100%; padding: 12px; background: rgba(255,255,255,0.05); border: 1px solid var(--border-sketch); color: white; border-radius: 4px; font-family: var(--font-mono);">
                </div>

                <div style="margin-bottom: 20px;">
                  <form-dynamic-list id="tagsList" name="tags" label="TAGS" placeholder="Type a tag name..." add-text="Add Another Tag" options='${JSON.stringify((this._orgTags || []).map(t => t.name).filter(t => !this._selectedTags.includes(t)))}' values='${JSON.stringify(this._selectedTags)}'></form-dynamic-list>
                </div>
                
                <div>
                  <label style="display: block; margin-bottom: 8px; font-size: 0.7rem; color: var(--text-muted); font-family: var(--font-mono);">VALUE ${this._editingSecret ? '(Leave blank to keep existing)' : ''}</label>
                  <textarea name="value" 
                            style="width: 100%; height: 120px; padding: 12px; background: rgba(255,255,255,0.05); border: 1px solid var(--border-sketch); color: white; border-radius: 4px; font-family: var(--font-mono); resize: none;" 
                            placeholder="Paste sensitive data here..." ${this._editingSecret ? '' : 'required'}></textarea>
                </div>
                
                <button type="submit" id="hidden-submit" style="display: none;"></button>
              </form>
            </div>

            <div slot="actions">
              <app-button variant="ghost" size="sm" id="btn-cancel" icon="x" data-tooltip="Cancel"></app-button>
              <app-button variant="primary" size="sm" id="btn-confirm" icon="check" data-tooltip="Save"></app-button>
            </div>
          </app-modal>
        `;
    } else {
        modalContainer.innerHTML = '';
    }

    this.setupListeners();
  }

  private setupListeners() {
    this.querySelector('#btn-new-secret')?.addEventListener('click', () => this.openCreateModal());
    this.querySelector('#btn-cancel')?.addEventListener('click', () => this.closeCreateModal());
    
    const modal = this.querySelector('#secret-modal');
    if (modal) {
      modal.addEventListener('close', () => this.closeCreateModal());
    }

    const confirmBtn = this.querySelector('#btn-confirm');
    if (confirmBtn) {
      confirmBtn.addEventListener('click', () => {
        const hiddenSubmit = this.querySelector('#hidden-submit') as HTMLButtonElement;
        hiddenSubmit?.click();
      });
    }

    const form = this.querySelector('#secret-form');
    if (form) {
      form.addEventListener('submit', (e) => this.handleSave(e));
    }

    this.querySelectorAll('.edit-btn').forEach(btn => {
      btn.addEventListener('click', () => {
        const name = (btn as HTMLElement).dataset.name;
        const secret = this._secrets.find(s => s.name === name);
        if (secret) this.openCreateModal(secret);
      });
    });

    this.querySelectorAll('.delete-btn').forEach(btn => {
      btn.addEventListener('click', () => {
        const name = (btn as HTMLElement).dataset.name;
        if (name) this.handleDelete(name);
      });
    });
  }

  private renderForbidden() {
    return `
      <div style="display: flex; flex-direction: column; align-items: center; justify-content: center; height: calc(100vh - 100px); gap: 24px; padding: 40px; text-align: center;">
        <div class="sketchy-card" style="max-width: 450px; padding: 40px; background: rgba(15, 23, 42, 0.4); border: 1px dashed rgba(255,255,255,0.1);">
          <div style="position: relative; z-index: 2;">
            <app-icon name="lock" size="48" style="color: var(--text-muted); margin-bottom: 20px; opacity: 0.8;"></app-icon>
            <h1 style="font-family: var(--font-main); font-size: 1.5rem; margin-bottom: 12px; color: var(--text-main); font-weight: 500;">Module Locked</h1>
            <p style="font-family: var(--font-main); color: var(--text-dim); line-height: 1.5; margin-bottom: 28px; font-size: 0.95rem;">
              The secrets module is not active for this organization.
            </p>
            ${this._isAdmin ? `
            <app-button variant="primary" size="sm" onclick="window.dispatchEvent(new CustomEvent('navigate', { detail: { view: 'organization', params: { tab: 'billing' } } }))" style="width: 100%;">
              Update Subscription
            </app-button>
            ` : ''}
          </div>
        </div>
      </div>
    `;
  }
}

if (!customElements.get('app-secrets')) {
  customElements.define('app-secrets', AppSecrets);
}
